import { Global } from "../constants/global";
import { Status } from "../constants/status";

class Logger {
  constructor() {
    this.environment = process.env.VITE_ENV || Global.UAT;
    this.isDevelopment =
      this.environment === Global.QAT || this.environment === Global.UAT;
    this.isProduction = this.environment === Global.PRD;
  }

  getTimestamp() {
    return new Date().toISOString();
  }

  maskSensitiveData(data) {
    if (!data) return data;
    try {
      let parsedData =
        typeof data === "string" ? JSON.parse(data) : { ...data };
      const sensitiveKeys = ["password", "confirmpassword", "token", "secret"];

      const recursivelyMask = (obj) => {
        Object.keys(obj).forEach((key) => {
          if (sensitiveKeys.some((sk) => key.toLowerCase().includes(sk))) {
            obj[key] = "***[REDACTED]***";
          } else if (typeof obj[key] === "object" && obj[key] !== null) {
            recursivelyMask(obj[key]);
          }
        });
      };

      recursivelyMask(parsedData);
      return typeof data === "string" ? JSON.stringify(parsedData) : parsedData;
    } catch (err) {
      return data;
    }
  }

  formatMessage(level, message, data) {
    const timestamp = this.getTimestamp();
    const safeData = this.maskSensitiveData(data);

    const formattedData = safeData
      ? `\n${JSON.stringify(safeData, null, this.isDevelopment ? 2 : 0)}`
      : "";

    if (this.isProduction && level !== "error") {
      return `${timestamp} [${level.toUpperCase()}] ${message}`;
    }

    return `${timestamp} [${level.toUpperCase()}] ${message}${formattedData}`;
  }

  log(level, message, data = null) {
    const formattedMessage = this.formatMessage(level, message, data);

    switch (level) {
      case "error":
        console.error(formattedMessage);
        break;
      case "warn":
        console.warn(formattedMessage);
        break;
      case "info":
        if (this.isDevelopment) {
          console.info(formattedMessage);
        }
        break;
      case "debug":
        if (this.isDevelopment) {
          console.debug(formattedMessage);
        }
        break;
      default:
        if (this.isDevelopment) {
          console.log(formattedMessage);
        }
    }
  }

  error(message, data = null) {
    this.log("error", message, data);
  }

  warn(message, data = null) {
    this.log("warn", message, data);
  }

  info(message, data = null) {
    this.log("info", message, data);
  }

  debug(message, data = null) {
    this.log("debug", message, data);
  }

  apiRequest(method, url, data = null) {
    if (this.isDevelopment) {
      this.debug(`API Request: ${method} ${url}`, data);
    }
  }

  apiResponse(method, url, response, duration = null) {
    if (this.isDevelopment) {
      const logData = {
        status: response.status,
        statusText: response.statusText,
        duration: duration ? `${duration}ms` : Status.UNKNOWN,
      };
      this.debug(`API Response: ${method} ${url}`, logData);
    }
  }
}

export const logger = new Logger();
